/** XeAgents.ai**/
import { useState } from "react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Switch } from "@/components/ui/switch";
import { Plus, Trash2, GripVertical, Wrench, CheckCircle, Clock, XCircle } from "lucide-react";
import { formatDate } from "@/lib/date-format";

export interface SetupPhase {
  id?: string;
  phaseNumber: number;
  phaseName: string;
  description: string;
  amount: number;
  isRequired: boolean;
  paymentStatus?: "pending" | "paid" | "waived" | "cancelled";
  paidAt?: string;
  dueDate?: string;
}

interface SetupPhasesManagerProps {
  phases: SetupPhase[];
  onChange: (phases: SetupPhase[]) => void;
  currency: string;
  currencySymbol: string;
  readOnly?: boolean;
}

// Pre-defined templates for quick setup
const PHASE_TEMPLATES = {
  standard: [
    { phaseName: "Initial Setup", description: "Account setup, basic configuration, and onboarding call", amount: 5000, isRequired: true },
  ],
  professional: [
    { phaseName: "Phase 1: Setup", description: "Account setup, basic configuration, and onboarding call", amount: 10000, isRequired: true },
    { phaseName: "Phase 2: Customization", description: "Custom agent configuration, voice setup, and flow design", amount: 15000, isRequired: true },
  ],
  enterprise: [
    { phaseName: "Phase 1: Discovery", description: "Requirements gathering, architecture planning, and project kickoff", amount: 15000, isRequired: true },
    { phaseName: "Phase 2: Development", description: "Custom integrations, API setup, and workflow automation", amount: 30000, isRequired: true },
    { phaseName: "Phase 3: Testing", description: "UAT, load testing, and quality assurance", amount: 10000, isRequired: true },
    { phaseName: "Phase 4: Training", description: "Team training, documentation, and handover", amount: 10000, isRequired: false },
  ],
};

export default function SetupPhasesManager({
  phases,
  onChange,
  currency,
  currencySymbol,
  readOnly = false,
}: SetupPhasesManagerProps) {
  const [expandedPhase, setExpandedPhase] = useState<number | null>(null);

  const addPhase = () => {
    const newPhase: SetupPhase = {
      phaseNumber: phases.length + 1,
      phaseName: `Phase ${phases.length + 1}`,
      description: "",
      amount: 0,
      isRequired: true,
    };
    onChange([...phases, newPhase]);
    setExpandedPhase(phases.length);
  };

  const removePhase = (index: number) => {
    const updated = phases.filter((_, i) => i !== index).map((phase, i) => ({
      ...phase,
      phaseNumber: i + 1,
    }));
    onChange(updated);
  };

  const updatePhase = (index: number, updates: Partial<SetupPhase>) => {
    const updated = phases.map((phase, i) =>
      i === index ? { ...phase, ...updates } : phase
    );
    onChange(updated);
  };

  const applyTemplate = (templateKey: keyof typeof PHASE_TEMPLATES) => {
    const template = PHASE_TEMPLATES[templateKey];
    const newPhases: SetupPhase[] = template.map((t, i) => ({
      ...t,
      phaseNumber: i + 1,
      paymentStatus: "pending" as const,
    }));
    onChange(newPhases);
  };

  const getTotalAmount = () => {
    return phases.reduce((sum, phase) => sum + (phase.amount || 0), 0);
  };

  const getRequiredAmount = () => {
    return phases.filter(p => p.isRequired).reduce((sum, phase) => sum + (phase.amount || 0), 0);
  };

  const getStatusBadge = (status?: string) => {
    switch (status) {
      case "paid":
        return <Badge className="bg-green-100 text-green-800 dark:bg-green-900 dark:text-green-300"><CheckCircle className="h-3 w-3 mr-1" />Paid</Badge>;
      case "pending":
        return <Badge className="bg-yellow-100 text-yellow-800 dark:bg-yellow-900 dark:text-yellow-300"><Clock className="h-3 w-3 mr-1" />Pending</Badge>;
      case "waived":
        return <Badge className="bg-blue-100 text-blue-800 dark:bg-blue-900 dark:text-blue-300">Waived</Badge>;
      case "cancelled":
        return <Badge className="bg-red-100 text-red-800 dark:bg-red-900 dark:text-red-300"><XCircle className="h-3 w-3 mr-1" />Cancelled</Badge>;
      default:
        return null;
    }
  };

  return (
    <div className="space-y-4">
      {/* Header with Templates */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-2">
          <Wrench className="h-5 w-5 text-muted-foreground" />
          <h4 className="font-medium">Setup/Development Phases</h4>
        </div>
        {!readOnly && (
          <div className="flex items-center gap-2">
            <span className="text-xs text-muted-foreground">Templates:</span>
            <Button
              type="button"
              variant="outline"
              size="sm"
              onClick={() => applyTemplate("standard")}
            >
              Standard
            </Button>
            <Button
              type="button"
              variant="outline"
              size="sm"
              onClick={() => applyTemplate("professional")}
            >
              Professional
            </Button>
            <Button
              type="button"
              variant="outline"
              size="sm"
              onClick={() => applyTemplate("enterprise")}
            >
              Enterprise
            </Button>
          </div>
        )}
      </div>

      {/* Phases List */}
      <div className="space-y-3">
        {phases.length === 0 ? (
          <div className="text-center py-6 border border-dashed rounded-lg">
            <Wrench className="h-8 w-8 mx-auto text-muted-foreground mb-2" />
            <p className="text-sm text-muted-foreground mb-3">
              No setup phases defined. Add phases for phased billing before subscription.
            </p>
            {!readOnly && (
              <div className="flex justify-center gap-2">
                <Button type="button" variant="outline" size="sm" onClick={addPhase}>
                  <Plus className="h-4 w-4 mr-1" />
                  Add Phase
                </Button>
                <Button type="button" variant="outline" size="sm" onClick={() => applyTemplate("standard")}>
                  Use Standard Template
                </Button>
              </div>
            )}
          </div>
        ) : (
          phases.map((phase, index) => (
            <Card key={index} className={expandedPhase === index ? "ring-2 ring-primary" : ""}>
              <CardHeader className="py-3 px-4">
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-3">
                    {!readOnly && (
                      <GripVertical className="h-4 w-4 text-muted-foreground cursor-move" />
                    )}
                    <div
                      className="flex items-center gap-2 cursor-pointer"
                      onClick={() => setExpandedPhase(expandedPhase === index ? null : index)}
                    >
                      <div className="h-6 w-6 rounded-full bg-primary/10 flex items-center justify-center text-xs font-bold text-primary">
                        {phase.phaseNumber}
                      </div>
                      <CardTitle className="text-sm">{phase.phaseName || `Phase ${phase.phaseNumber}`}</CardTitle>
                    </div>
                    {phase.isRequired && (
                      <Badge variant="outline" className="text-xs">Required</Badge>
                    )}
                    {getStatusBadge(phase.paymentStatus)}
                  </div>
                  <div className="flex items-center gap-3">
                    <span className="font-medium">
                      {currencySymbol}{phase.amount.toLocaleString()}
                    </span>
                    {!readOnly && (
                      <Button
                        type="button"
                        variant="ghost"
                        size="sm"
                        onClick={() => removePhase(index)}
                        className="text-red-500 hover:text-red-700"
                      >
                        <Trash2 className="h-4 w-4" />
                      </Button>
                    )}
                  </div>
                </div>
              </CardHeader>

              {expandedPhase === index && (
                <CardContent className="pt-0 pb-4 px-4">
                  <div className="grid gap-4">
                    <div className="grid grid-cols-2 gap-4">
                      <div className="space-y-2">
                        <Label>Phase Name</Label>
                        <Input
                          value={phase.phaseName}
                          onChange={(e) => updatePhase(index, { phaseName: e.target.value })}
                          placeholder="e.g., Initial Setup"
                          disabled={readOnly}
                        />
                      </div>
                      <div className="space-y-2">
                        <Label>Amount ({currencySymbol})</Label>
                        <Input
                          type="number"
                          min="0"
                          value={phase.amount}
                          onChange={(e) => updatePhase(index, { amount: parseFloat(e.target.value) || 0 })}
                          disabled={readOnly}
                        />
                      </div>
                    </div>

                    <div className="space-y-2">
                      <Label>Description / Deliverables</Label>
                      <Textarea
                        value={phase.description}
                        onChange={(e) => updatePhase(index, { description: e.target.value })}
                        placeholder="Describe the deliverables for this phase..."
                        rows={2}
                        disabled={readOnly}
                      />
                    </div>

                    <div className="flex items-center justify-between">
                      <div className="flex items-center gap-2">
                        <Switch
                          checked={phase.isRequired}
                          onCheckedChange={(checked) => updatePhase(index, { isRequired: checked })}
                          disabled={readOnly}
                        />
                        <Label className="text-sm">
                          Required before subscription starts
                        </Label>
                      </div>
                      {phase.dueDate && (
                        <span className="text-xs text-muted-foreground">
                          Due: {formatDate(phase.dueDate, 'MMM d, yyyy')}
                        </span>
                      )}
                    </div>
                  </div>
                </CardContent>
              )}
            </Card>
          ))
        )}
      </div>

      {/* Add Phase Button */}
      {!readOnly && phases.length > 0 && (
        <Button type="button" variant="outline" size="sm" onClick={addPhase}>
          <Plus className="h-4 w-4 mr-1" />
          Add Another Phase
        </Button>
      )}

      {/* Summary */}
      {phases.length > 0 && (
        <div className="rounded-lg border bg-muted/30 p-4">
          <div className="grid grid-cols-3 gap-4 text-sm">
            <div>
              <span className="text-muted-foreground">Total Phases:</span>
              <span className="ml-2 font-medium">{phases.length}</span>
            </div>
            <div>
              <span className="text-muted-foreground">Required Amount:</span>
              <span className="ml-2 font-medium text-amber-600">
                {currencySymbol}{getRequiredAmount().toLocaleString()}
              </span>
            </div>
            <div>
              <span className="text-muted-foreground">Total Amount:</span>
              <span className="ml-2 font-medium">
                {currencySymbol}{getTotalAmount().toLocaleString()}
              </span>
            </div>
          </div>
          <p className="text-xs text-muted-foreground mt-2">
            Required phases must be paid before the monthly subscription begins.
          </p>
        </div>
      )}
    </div>
  );
}
